import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Constant } from '../shared/constants/constant.class';
import { StorageService } from './storage.service';

/**
 * Giữ accessToken đăng nhập của app.
 *
 * VÌ SAO PHẢI CÓ SERVICE NÀY:
 * Token chỉ được lưu ở màn đăng nhập. Nhưng người dùng đang cài app sẵn thì
 * KHÔNG bao giờ đi qua màn đó nữa — login.page.ts thấy storage còn
 * selected_student là nhảy thẳng vào /main/tab1. Nếu không có cơ chế lấy ngầm,
 * sau khi cập nhật app thì toàn bộ phụ huynh hiện tại đều không có token và
 * chức năng Học bạ điện tử sẽ báo "hết phiên đăng nhập" với tất cả mọi người.
 *
 * Service này tự đăng nhập lại ngầm bằng tài khoản đã lưu trong LMS_CREDENTIAL,
 * đúng cách mà bao-bai.service.ts đang làm cho LMS token.
 *
 * Token lưu ở localStorage (đồng bộ) vì BaseService.createHeaders() đọc ở đó và
 * không await được Ionic Storage.
 *
 * ===========================================================================
 * HAI ĐƯỜNG LẤY TOKEN — VÌ SAO KHÔNG LÀM refreshToken RIÊNG
 * ===========================================================================
 *   1. renew()       — gia hạn token cũ. Chạy cho MỌI tài khoản, kể cả SSO.
 *   2. silentLogin() — đăng nhập ngầm bằng mật khẩu đã lưu. Chỉ tài khoản
 *                      thường; tài khoản SSO của Sở không có mật khẩu nào
 *                      được lưu.
 *
 * Với tài khoản thường, silentLogin() ĐÃ CHÍNH LÀ refresh — thêm một cặp
 * access/refresh token nữa không giải quyết thêm gì, chỉ thêm một thứ phải lưu,
 * phải xoay vòng, phải thu hồi.
 *
 * Lỗ thật nằm ở tài khoản SSO: không mật khẩu -> hết token là buộc đăng nhập
 * lại bằng tay. renew() bịt đúng lỗ đó, và không cần lưu gì phía server.
 *
 * Gia hạn KHÔNG vô hạn: server chặn trần tuyệt đối kể từ lần đăng nhập đầu
 * (MobileTokenHanTuyetDoiNgay, mặc định 180 ngày). Quá trần thì server trả
 * code 'REAUTH_REQUIRED' và lúc đó mới thật sự phải đăng nhập lại.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {

  /** Gộp các lời gọi song song lại làm một, tránh gọi login nhiều lần cùng lúc */
  private inFlight: Promise<string> | null = null;

  constructor(
    private httpClient: HttpClient,
    private localStorage: StorageService
  ) { }

  /** Đọc token hiện có. Rỗng nghĩa là chưa có. */
  get token(): string {
    try {
      return localStorage.getItem(Constant.TOKEN) || '';
    } catch {
      return '';
    }
  }

  save(token: string) {
    try {
      if (token) {
        localStorage.setItem(Constant.TOKEN, token);
      } else {
        localStorage.removeItem(Constant.TOKEN);
      }
    } catch {
      // Chế độ riêng tư / storage bị chặn: bỏ qua, API sẽ trả 401 và màn
      // học bạ hướng dẫn người dùng đăng nhập lại.
    }
  }

  clear() {
    this.save('');
  }

  /**
   * Bảo đảm có token.
   *
   * @param force true khi vừa nhận 401 — bỏ token cũ, xin cái mới.
   * @returns token, hoặc '' nếu không lấy được (chưa lưu tài khoản, sai mật khẩu,
   *          mất mạng, hoặc server chưa trả accessToken).
   */
  ensureToken(force = false): Promise<string> {
    if (!force && this.token) {
      return Promise.resolve(this.token);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    const done = (t: string) => {
      this.inFlight = null;
      return t;
    };
    this.inFlight = this.layTokenMoi().then(done, (e) => {
      this.inFlight = null;
      throw e;
    });
    return this.inFlight;
  }

  /**
   * Số ngày còn lại của token hiện tại. Âm nghĩa là đã hết hạn.
   * Trả null nếu không đọc được (không có token, hoặc token dạng lạ).
   *
   * Đọc được exp mà không cần khoá vì phần đầu token chỉ là base64url của chuỗi
   * `userId|userType|schoolId|exp|iat` — không mã hoá, chỉ ký. App chỉ ĐỌC để
   * biết khi nào nên gia hạn; mọi việc kiểm tra thật vẫn ở server.
   */
  ngayConLai(): number | null {
    const t = this.token;
    if (!t) {
      return null;
    }
    try {
      const phanDau = t.split('.')[0];
      if (!phanDau) {
        return null;
      }
      // base64url -> base64
      let b64 = phanDau.replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4 !== 0) {
        b64 += '=';
      }
      const o = atob(b64).split('|');
      // o[3] = exp (unix giây). Token cũ 4 đoạn cũng có o[3].
      if (o.length < 4) {
        return null;
      }
      const exp = parseInt(o[3], 10);
      if (!exp) {
        return null;
      }
      return (exp * 1000 - Date.now()) / 86400000;
    } catch {
      return null;
    }
  }

  /**
   * Gọi lúc mở app: token gần hết hạn thì âm thầm gia hạn.
   *
   * VÌ SAO LÀM CHỦ ĐỘNG, KHÔNG CHỜ HẾT HẠN: người dùng mở app vài tuần một lần
   * thì cứ mỗi lần mở lại đẩy hạn thêm 30 ngày, token thực tế không bao giờ hết.
   * Chờ đến khi hết hạn mới xử lý thì vẫn có một lần người dùng bị gián đoạn.
   *
   * Không ném lỗi, không chặn luồng khởi động — hỏng thì để nguyên token cũ.
   */
  async giaHanNeuGanHan(nguongNgay = 7): Promise<void> {
    const conLai = this.ngayConLai();
    if (conLai === null) {
      return;
    }
    // Hết hạn rồi cũng thử: server còn cho gia hạn trong khoảng ân hạn.
    if (conLai > nguongNgay) {
      return;
    }
    try {
      await this.renew();
    } catch {
      // bỏ qua
    }
  }

  /**
   * Gia hạn token tại chỗ, KHÔNG cần mật khẩu.
   *
   * Đây là thứ duy nhất cứu được tài khoản đăng nhập bằng SSO của Sở: app không
   * giữ mật khẩu của họ nên không đăng nhập ngầm lại được.
   *
   * Trả về token mới, hoặc '' nếu không gia hạn được.
   */
  async renew(): Promise<string> {
    const cu = this.token;
    if (!cu) {
      return '';
    }
    try {
      const res: any = await this.httpClient.post(
        environment.baseUrl + '/api/Mobile/RenewAccessToken',
        {},
        { headers: new HttpHeaders({ Authorization: 'Bearer ' + cu }) }
      ).toPromise();

      if (!res || +res.result !== 1 || !res.accessToken) {
        // code = 'REAUTH_REQUIRED' nghĩa là hết đường gia hạn: quá hạn tuyệt đối
        // kể từ lần đăng nhập đầu, hoặc token sai. Lúc đó mới cần mật khẩu.
        return '';
      }
      this.save(res.accessToken);
      return res.accessToken;
    } catch {
      return '';
    }
  }

  /**
   * Lấy token mới theo thứ tự ưu tiên:
   *   1. GIA HẠN token cũ      — chạy được cho MỌI tài khoản, kể cả SSO.
   *   2. ĐĂNG NHẬP NGẦM        — chỉ được với tài khoản có lưu mật khẩu.
   *
   * Thứ tự này quan trọng: gia hạn nhẹ hơn (một request, không gửi mật khẩu đi)
   * và là đường duy nhất dùng được cho tài khoản SSO.
   */
  private async layTokenMoi(): Promise<string> {
    const giaHan = await this.renew();
    if (giaHan) {
      return giaHan;
    }
    return this.silentLogin();
  }

  /** Đăng nhập lại ngầm bằng tài khoản đã lưu. Không ném lỗi ra ngoài. */
  private async silentLogin(): Promise<string> {
    const credential = await this.localStorage.get(Constant.LMS_CREDENTIAL);
    if (!credential || !credential.username) {
      // Tài khoản đăng nhập bằng SSO Sở không lưu mật khẩu, nên không tự lấy
      // lại được. Những tài khoản đó buộc phải đăng nhập lại bằng tay.
      return '';
    }

    const deviceInfo = (await this.localStorage.get('deviceInfo')) || {};
    const regId = (await this.localStorage.get('fcmToken')) || 'web token';

    // deviceInfo.uuid của bản app cũ (đã cài trên máy người dùng) có thể còn là
    // OBJECT { identifier: '...' } do lỗi ở app.component.ts trước đây — giá trị
    // đó đã nằm trong storage của họ rồi, sửa app.component.ts không dọn được.
    // Gửi object lên thì server không đọc được body và đăng nhập ngầm hỏng
    // câm lặng -> cả tính năng Học bạ báo "hết phiên" với mọi người đang dùng app.
    // Nên phải chuẩn hoá ở đây nữa.
    const uuid: any = deviceInfo.uuid;
    const deviceId = (uuid && typeof uuid === 'object')
      ? (uuid.identifier || uuid.uuid || '')
      : (uuid || '');

    const body = {
      username: credential.username,
      password: credential.password,
      regId,
      deviceId,
      deviceName: deviceInfo.model || 'LA'
    };

    try {
      const res: any = await this.httpClient.post(
        environment.baseUrl + '/api/Mobile/MobileLoginV2',
        body,
        { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
      ).toPromise();

      if (!res || +res.result === 0) {
        // Mật khẩu đã đổi trên web -> tài khoản lưu không còn đúng.
        return '';
      }
      const token = res.accessToken || res.access_token || '';
      this.save(token);
      return token;
    } catch {
      return '';
    }
  }
}
